import type { PlayLimits, PlaySessionView } from '../play/types';
import { validateLimits } from '../play/limits';
import { steeringReady } from '../play/planner';
/**
 * DemoWorld — credential-free, deterministic, IN-MEMORY simulation of RUNNER (change order 4.0). Clearly labelled
 * DEMO everywhere and makes no network request at all.
 *
 * What is real here: the canonical movement/collision engine and road-budget effects (RunSimulator, the pinned engine
 * rules), course expansion, the five-bucket waterfall split (WaterfallLib mirror), run funding arithmetic
 * (RunnerTreasury.reserveRun mirror), identity base traits and the base renderer image, and the Golden Shoe draw — the
 * same reference implementations checked against the Solidity contracts.
 * What is simulated: trades (your buttons + named simulated traders), the pool, randomness (a published demo seed),
 * other holders, balances and a labelled demo sponsorship. No wallet, no chain, no real funds.
 */
import { keccak256, toHex, type Hex } from 'viem';
import {
  DEFAULTS,
  DEFAULT_ECONOMIC_PROFILE,
  EncounterKind,
  IDENTITY_TRAIT_SEED,
  RoadEffectKind,
  RunSimulator,
  baseTraits,
  chooseInput,
  defaultFormations,
  earliestCrossingStep,
  economicProfileSummary,
  expand,
  findEntry,
  fundableBudget,
  runReservation,
  handlerParams,
  initialRoad,
  movementParams,
  netForInput,
  roadParams,
  runLimits,
  ticket,
  traitLabels,
  TRAIT_TITLES,
  waterfallPolicy,
  waterfallSplit,
  type Entry,
  type RoadState,
} from '@runner/simulation';
import { applyBuy, applySell, buybackOut, price, quoteBuy, quoteSell, type PoolState } from './pool';
import { identitySvg, jsonDataUri, previewWaterfall, svgDataUri } from '../model';
import type {
  AccountRunners,
  Achievements,
  BaseRewardsView,
  CourseObjectView,
  FeedItem,
  IdentityDetail,
  IdentitySummary,
  JournalItem,
  OperatorSession,
  Phase,
  QuoteView,
  RewardsAccount,
  RewardsBreakdown,
  RunView,
  Stage,
  StepView,
  TradeRequest,
  TreasurySnapshot,
  TxView,
  VersionsView,
  WaterfallSimRequest,
  WaterfallSimulation,
  WorldActions,
  WorldView,
} from '../types';

const E18 = 10n ** 18n;
const REF_SUPPLY = 1_000_000_000n * E18;
/** Simulated other holders' principal in the vault (labelled demo). */
const DEMO_HOLDERS = 120_000_000n * E18;
const ZERO_HASH = `0x${'0'.repeat(64)}` as Hex;
const ZERO_ADDR = `0x${'0'.repeat(40)}` as Hex;
const RESULT_HOLD_WINDOWS = 3;
const REPLAY_GAP_WINDOWS = 2;

export const YOU = '0x00000000000000000000000000000000000000Fe' as Hex;

interface DemoShoe {
  shoeId: number;
  objectIndex: number;
  amount: bigint;
  cutoff: number;
  entries: Entry[];
  status: 'Open' | 'Sealed' | 'Collected' | 'Missed' | 'Drawing' | 'Claimable' | 'Unawarded';
  collected: boolean | null;
  winner: Hex | null;
  paid: boolean;
}

interface Bot {
  name: string;
  address: Hex;
  style: 'buyer' | 'seller' | 'swing' | 'pilot';
}

/** One block of the user's principal: ordinary or the backing of one Runner. */
interface Lot {
  amount: bigint;
  eligibleFrom: number;
  tokenId: number | null;
}

type DemoStreamName = 'direct' | 'deferred' | 'run';
type DemoEntitlementStatus = 'claimed' | 'claimable' | 'locked' | 'not-final';

/** The demo account's entitlement of one settled epoch (same rules as RunnerRewards: sealed weight, three streams). */
interface DemoEntitlement {
  epoch: number;
  weight: bigint;
  global: bigint;
  /** Your Runners whose backing counted in this epoch (part of `weight`, never added to it). */
  runners: { tokenId: number; backing: bigint }[];
  streams: { stream: DemoStreamName; credited: bigint; amount: bigint; status: DemoEntitlementStatus }[];
}

interface DemoIdentity {
  id: number;
  owner: Hex | null;
  backing: bigint;
  eligibleFrom: number;
  mintedEpoch: number;
  mintedAt: number;
  origin: 'deposit' | 'convert';
  status: 'live' | 'redeemed' | 'converted';
  transfers: number;
  history: IdentityDetail['history'];
  /** Equipment keyed by (token, owner): a new owner starts with the base look. */
  equipment: Map<string, number[]>;
}

interface DemoRunRecord {
  id: number;
  cohortEpoch: number;
  budget: bigint;
  road: RoadState;
  effects: { step: number; effect: number; to: 'holders' | 'forge'; requested: bigint; assigned: bigint }[];
  open: boolean;
  provision: bigint;
}

const WARDROBE = [
  { itemId: 1, slot: 0, slotName: 'shoes', name: 'Ember trainers' },
  { itemId: 2, slot: 1, slotName: 'jacket', name: 'Harvest windbreaker' },
  { itemId: 3, slot: 2, slotName: 'bib', name: 'Gold bib ribbon' },
  { itemId: 4, slot: 3, slotName: 'animation', name: 'Victory wave' },
];
export const WARDROBE_SLOTS = ['shoes', 'jacket', 'bib', 'animation'];

/** Small deterministic PRNG (mulberry32) for simulated traders. */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const s = (v: bigint | number) => v.toString();

export interface DemoOptions {
  seed?: number;
  windowSeconds?: number;
  epochWindows?: number;
  autoplay?: boolean;
  crowd?: boolean;
  /** 'warmup': no demo sponsorship — runs must wait for fee income (shows the warm-up phase). */
  scenario?: 'default' | 'warmup';
}

export class DemoWorld implements WorldActions {
  readonly m = movementParams();
  readonly h = handlerParams(18);
  readonly road = roadParams(18);
  readonly policy = waterfallPolicy();
  readonly windowSeconds: number;
  readonly epochWindows: number;
  readonly backing = DEFAULTS.identityBacking;
  readonly cap = Number(DEFAULTS.identityCap);
  private readonly rand: () => number;
  private readonly sessionSeed: number;
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private paused = false;
  private clockMs = 0;
  private lastReal = 0;
  private version = 0;
  crowdEnabled: boolean;

  // Market
  pool: PoolState = { runner: 500_000_000n * E18, quote: 50_000n * E18, lpFeePips: 3000n, hookFeeBps: 50n };
  runnerSupply = REF_SUPPLY;

  // Treasury (RunnerTreasury mirror, demo values)
  private accrual = new Map<number, bigint>();
  receivable = 0n;
  operations = 0n;
  prizeReserve = 0n;
  forgeReserve = 0n;
  roadReserve = 0n;
  executionReserve = 0n;
  private sponsorship = { prize: 0n, forge: 0n, road: 0n, operations: 0n };
  private totals = {
    accrued: 0n,
    settled: 0n,
    toRewards: 0n,
    toRunCohorts: 0n,
    prizeReserved: 0n,
    prizeReturned: 0n,
    forgeAuthorized: 0n,
    forgeReturned: 0n,
    opsSpent: 0n,
    roadReserved: 0n,
    roadToHolders: 0n,
    roadToForge: 0n,
    roadReturned: 0n,
  };
  /** Settled epochs only (open / unsettled rows are derived from `accrual` in treasuryView). */
  private waterfallEpochs: TreasurySnapshot['waterfall']['epochs'] = [];
  /** The demo account's entitlements per settled epoch (rewards breakdown). */
  private entitlements = new Map<number, DemoEntitlement>();
  private ledger: TreasurySnapshot['road']['ledger'] = [];
  /** Holder-directed road value per sealed cohort epoch, credited once its runs closed and cohort + 2 ≤ current. */
  private cohortPending = new Map<number, bigint>();
  private cohortOpenRuns = new Map<number, number>();
  private runFinalizedCount = 0;
  private finalizedEpochs = 0;
  /** Sealed weights per completed epoch: the user's weight and the global weight. */
  private sealed = new Map<number, { you: bigint; global: bigint; idTokens: number[] }>();

  // Forge pipeline
  authorized = 0n;
  spent = 0n;
  purchased = 0n;
  inventory = 0n;
  burned = 0n;
  private forgeStage: 'none' | 'authorized' | 'purchased' | 'burned' = 'none';
  private forgeNote = 'No Forge authorization yet.';

  // Game
  private runId = 0;
  private run: RunView | null = null;
  private runs = new Map<number, DemoRunRecord>();
  private sim: RunSimulator | null = null;
  private steps: StepView[] = [];
  private modifierCount = 0n;
  private stage: 'idle' | 'funding' | 'preview' | 'active' | 'result' | 'gap' = 'idle';
  private stageUntilWindow = 1;
  private lastReplayRunId: number | null = null;
  private shoe: DemoShoe | null = null;
  private shoeCount = 0;
  private windowNet = new Map<number, { net: bigint; volume: bigint; swaps: number; yours: bigint }>();
  private lastProcessedWindow = -1;
  private steeredRuns = new Set<number>();

  // People
  private bots: Bot[];
  user = { quote: 1_000n * E18, runner: 5_000_000n * E18, claimableDirect: 0n, lockedDeferred: 0n, claimableDeferred: 0n, claimableRun: 0n, prizeOwed: 0n, prizePaid: 0n };
  private lots: Lot[] = [];
  private claimsLog: RewardsAccount['recentClaims'] = [];
  private identities = new Map<number, DemoIdentity>();
  private minted = 0;
  private achievementsLog: Achievements['achievements'] = [];
  private feed: FeedItem[] = [];
  private journal: JournalItem[] = [];
  private feedSeq = 0;

  constructor(opts: DemoOptions = {}) {
    this.sessionSeed = opts.seed ?? 20261009;
    this.rand = mulberry32(this.sessionSeed);
    this.windowSeconds = opts.windowSeconds ?? 3;
    this.epochWindows = opts.epochWindows ?? 8;
    this.crowdEnabled = opts.crowd ?? true;
    const names = ['Pacer', 'Hill Climber', 'Tempo', 'Long Stride', 'Split Timer', 'Cadence'];
    this.bots = names.map((n, i) => ({
      name: n,
      address: keccak256(toHex(`demo-bot-${i}`)).slice(0, 42) as Hex,
      style: (['buyer', 'seller', 'swing', 'swing', 'pilot', 'pilot'] as const)[i],
    }));
    if (opts.scenario !== 'warmup') {
      // Labelled DEMO sponsorship (like the local deployment's classified donation) so the first runs and the first
      // Golden Shoe can be funded before fees exist. Never fee revenue.
      this.sponsor('road', 6n * E18);
      this.sponsor('prize', 2n * E18);
      this.sponsor('operations', 1n * E18);
    }
    // Two Runners held by simulated holders (part of DEMO_HOLDERS) so the collection is not empty.
    for (const b of [this.bots[0], this.bots[2]]) this.mintFor(b.address, 'deposit', 0);
    this.push('system', 'Demo started — simulated traders, simulated pool, demo randomness. No real funds.', 'neutral');
    this.addJournal(
      'Warming up in demo mode',
      'This is a practice track: the traders and money are simulated, but RUNNER moves and pays out by the same rules as the real contracts.',
      'release',
      [],
    );
    if (opts.autoplay !== false) this.start();
  }

  private sponsor(bucket: 'road' | 'prize' | 'operations', amount: bigint) {
    this.sponsorship[bucket] += amount;
    if (bucket === 'road') {
      this.roadReserve += amount;
      this.ledgerPush({ kind: 'sponsored', amount, runId: null, epoch: null, policyId: null, cohortEpoch: null });
    } else if (bucket === 'prize') this.prizeReserve += amount;
    else this.operations += amount;
  }

  // ------------------------------------------------------------------------------------------------
  // Clock
  // ------------------------------------------------------------------------------------------------

  start() {
    if (this.timer) return;
    this.lastReal = Date.now();
    this.timer = setInterval(() => this.tick(), 100);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  setPaused(p: boolean) {
    this.paused = p;
    this.emit();
  }

  get isPaused() {
    return this.paused;
  }

  nowSeconds(): number {
    return this.clockMs / 1000;
  }

  currentWindow(): number {
    return Math.floor(this.nowSeconds() / this.windowSeconds);
  }

  epochOfWindow(w: number) {
    return Math.floor(w / this.epochWindows);
  }

  currentEpoch(): number {
    return this.epochOfWindow(this.currentWindow());
  }

  /** Advance simulated time (also used by tests to fast-forward deterministically). */
  advanceMs(ms: number) {
    this.clockMs += ms;
    const w = this.currentWindow();
    while (this.lastProcessedWindow < w - 1) {
      this.lastProcessedWindow += 1;
      this.onWindowClosed(this.lastProcessedWindow);
    }
    this.emit();
  }

  private tick() {
    const now = Date.now();
    const dt = Math.min(now - this.lastReal, 1000);
    this.lastReal = now;
    if (this.paused) return;
    this.advanceMs(dt);
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    this.version++;
    for (const l of this.listeners) l();
  }

  // ------------------------------------------------------------------------------------------------
  // Window processing: crowd trades → step resolution → settlement → run lifecycle
  // ------------------------------------------------------------------------------------------------

  private onWindowClosed(w: number) {
    if (this.crowdEnabled) this.crowdTrades(w);
    if (this.stage === 'active' && this.run && this.sim) {
      const stepWindow = this.run.startWindow + this.sim.stepIndex;
      if (stepWindow === w) this.resolveStep(w);
    }
    this.advanceForge();
    const nextEpoch = this.epochOfWindow(w + 1);
    if (nextEpoch !== this.epochOfWindow(w)) this.settleEpoch(this.epochOfWindow(w));
    this.creditCohorts(nextEpoch);
    this.lifecycle(w + 1);
  }

  fundableNow(): bigint {
    return fundableBudget(this.roadReserve, this.operations, this.road.runBudget, this.road.minRunBudget, this.road.opsProvision);
  }

  private lifecycle(nextWindow: number) {
    if (this.stage === 'result' && nextWindow >= this.stageUntilWindow) {
      this.stage = 'gap';
      this.stageUntilWindow = nextWindow + REPLAY_GAP_WINDOWS;
    }
    if ((this.stage === 'idle' || this.stage === 'gap') && nextWindow >= this.stageUntilWindow) {
      const budget = this.fundableNow();
      if (budget > 0n) this.requestCourse(nextWindow, budget);
    } else if (this.stage === 'funding' && nextWindow >= this.stageUntilWindow) {
      this.startRun(nextWindow);
    } else if (this.stage === 'preview' && this.run && nextWindow >= this.run.startWindow) {
      this.stage = 'active';
    }
  }

  /** RunnerGame.requestCourse + RunnerTreasury.reserveRun: the run is funded before its seed is requested. */
  private requestCourse(nextWindow: number, budget: bigint) {
    this.runId += 1;
    const epoch = this.epochOfWindow(nextWindow);
    const cohortEpoch = epoch > 0 ? epoch - 1 : 0;
    this.roadReserve -= budget;
    this.operations -= this.road.opsProvision;
    this.executionReserve += this.road.opsProvision;
    this.totals.roadReserved += budget;
    this.cohortOpenRuns.set(cohortEpoch, (this.cohortOpenRuns.get(cohortEpoch) ?? 0) + 1);
    const rec: DemoRunRecord = { id: this.runId, cohortEpoch, budget, road: initialRoad(budget, this.modifierCount), effects: [], open: true, provision: this.road.opsProvision };
    this.runs.set(this.runId, rec);
    this.ledgerPush({ kind: 'reserved', amount: budget, runId: this.runId, epoch, policyId: 1, cohortEpoch });
    // Runners counted in this sealed cohort: their holders get the 'runner-in-cohort' achievement.
    const sealedCohort = this.sealed.get(cohortEpoch);
    for (const id of sealedCohort?.idTokens ?? []) {
      const t = this.identities.get(id);
      if (t && this.isYou(t.owner)) this.achieve('runner-in-cohort', { runId: this.runId, tokenId: id });
    }
    this.stage = 'funding';
    this.stageUntilWindow = nextWindow + 1;
    this.run = {
      id: this.runId,
      status: 'awaiting-randomness',
      abandoned: false,
      configVersion: 1,
      engineVersion: 1,
      engineAddress: null,
      cohortEpoch,
      policyId: 1,
      budget,
      exhibition: false, // the demo plays the fixture configuration (minimum budget > 0): every demo run is funded
      exitRoute: null,
      reachedFinish: null,
      startWindow: 0,
      maxSteps: 0,
      courseLength: 0,
      course: [],
      seed: null,
      seedLabel: 'DEMO SEED (published, not random)',
      courseHash: null,
      shoe: null,
    };
    this.steps = [];
    this.sim = null;
    this.push('run', `Run ${this.runId} is funded: ${fmt(budget)} DEMO-Q set aside for this run (depositors of round ${cohortEpoch}). The course is being drawn.`, 'neutral');
  }

  private startRun(nextWindow: number) {
    const run = this.run!;
    const rec = this.runs.get(run.id)!;
    const seed = keccak256(toHex(`runner-demo-${this.sessionSeed}-run-${run.id}`));
    const { course, length } = expand(seed, defaultFormations(), this.m.xMax);
    const { maxSteps, maxPace } = runLimits(length, this.m, this.h);
    const startWindow = nextWindow + Number(DEFAULTS.previewWindows);
    const objs: CourseObjectView[] = course.map((o, i) => ({ ...o, index: i, funded: false }));
    let shoeIndex: number | null = null;
    this.shoe = null;
    objs.forEach((o, i) => {
      if (o.kind !== EncounterKind.GOLDEN_SHOE || shoeIndex !== null) return;
      const amount = this.h.shoePrize < this.prizeReserve ? this.h.shoePrize : this.prizeReserve;
      if (amount >= this.h.shoeMinPrize && amount > 0n) {
        this.prizeReserve -= amount;
        this.totals.prizeReserved += amount;
        const earliest = Number(earliestCrossingStep(o.y, maxPace));
        this.shoe = { shoeId: ++this.shoeCount, objectIndex: i, amount, cutoff: (startWindow + earliest) * this.windowSeconds, entries: [], status: 'Open', collected: null, winner: null, paid: false };
        o.funded = true;
        o.param = 1n;
        shoeIndex = i;
      } else {
        this.push('prize', 'Golden Shoe left unfunded: the prize reserve is below the minimum. Fewer encounters, not invented money.', 'warn');
      }
    });
    Object.assign(run, { status: 'active', startWindow, maxSteps, courseLength: Number(length), course: objs, seed });
    this.sim = new RunSimulator(
      course.map((o, i) => (i === shoeIndex ? { ...o, param: 1n } : o)),
      { m: this.m, h: this.h, road: this.road, refSupply: REF_SUPPLY, rules: 1 },
      BigInt(startWindow),
      maxSteps,
      initialRoad(rec.budget, this.modifierCount),
      shoeIndex,
    );
    this.steps = [];
    this.stage = 'preview';
    this.push('run', `Run ${run.id}: the course is set — ${objs.length} things on a ${Number(length)}-unit road. Steering opens in 2 windows.`, 'neutral');
    const funded = this.shoe as DemoShoe | null;
    if (funded) this.push('prize', `Golden Shoe on the road: a ${fmt(funded.amount)} DEMO-Q prize, paid for up front.`, 'gold');
    this.addJournal(
      `Run ${run.id} is on the board`,
      `The course was set before anyone could steer. This run moves ${fmt(rec.budget)} DEMO-Q; fee rewards aren't at stake. ${funded ? `A Golden Shoe worth ${fmt(funded.amount)} DEMO-Q is on the road.` : 'No Golden Shoe this time — the prize reserve was too small.'}`,
      'run',
      [`seed ${seed.slice(0, 10)}…`],
    );
  }

  private resolveStep(w: number) {
    const sim = this.sim!;
    const run = this.run!;
    const rec = this.runs.get(run.id)!;
    const input = this.windowNet.get(w) ?? { net: 0n, volume: 0n, swaps: 0, yours: 0n };
    if (input.yours !== 0n && !this.steeredRuns.has(run.id)) {
      this.steeredRuns.add(run.id);
      this.achieve('run-steered', { runId: run.id, step: sim.stepIndex });
    }
    const windowClose = BigInt((w + 1) * this.windowSeconds);
    const r = sim.resolve(input.net, input.volume, windowClose);
    const encounters = r.crossings.map((c) => ({ objectIndex: c.index, kind: c.kind, hit: c.hit, xCross: c.xCross, amount: 0n }));
    for (const c of r.crossings) {
      if (c.kind === EncounterKind.FORK) {
        const right = r.state.route === 2;
        this.push('encounter', right ? 'Fork: RUNNER took the right branch — a Harvest exit sends what is left of this run’s budget to holders.' : 'Fork: RUNNER took the left branch — a Forge exit sends what is left of this run’s budget to the Forge reserve.', right ? 'harvest' : 'forge');
      }
      if (c.kind === EncounterKind.BARRIER) this.push('encounter', c.hit ? 'Barrier hit: RUNNER stumbles and slows for 2 steps.' : 'Barrier cleared.', c.hit ? 'warn' : 'neutral');
      if (c.kind === EncounterKind.FORGE_GATE && c.hit && input.yours !== 0n) this.achieve('forge-gate', { runId: run.id, step: r.stepIndex });
    }
    for (const eff of r.effects) {
      if (eff.type === 'road') {
        rec.effects.push({ step: r.stepIndex, effect: eff.effect, to: eff.toHolders ? 'holders' : 'forge', requested: eff.requested, assigned: eff.assigned });
        if (eff.toHolders) {
          this.cohortPending.set(rec.cohortEpoch, (this.cohortPending.get(rec.cohortEpoch) ?? 0n) + eff.assigned);
          this.totals.roadToHolders += eff.assigned;
        } else {
          this.forgeReserve += eff.assigned;
          this.totals.roadToForge += eff.assigned;
        }
        const what = eff.effect === RoadEffectKind.WATER ? 'Water slice' : eff.effect === RoadEffectKind.OVERHEAT ? 'Overheat slice' : 'Exit allocation';
        this.push('run', `${what}: ${fmt(eff.assigned)} DEMO-Q of this run's money → ${eff.toHolders ? `depositors (round ${rec.cohortEpoch})` : 'the Forge'}.`, eff.toHolders ? 'harvest' : 'forge');
      } else if (eff.type === 'modifierStarted') {
        this.push('encounter', `Burning barricade hit: Overheat for ${eff.steps} steps — each step sends ${Number(eff.sliceBps) / 100}% of this run's money to the Forge (max ${fmt(eff.cap)}). Fee rewards are untouched.`, 'forge');
      } else if (eff.type === 'modifierEnded') {
        this.push('encounter', 'Overheat bias ended.', 'neutral');
      } else if (eff.type === 'releaseDeferred') {
        this.user.claimableDeferred += this.user.lockedDeferred;
        this.user.lockedDeferred = 0n;
        for (const ent of this.entitlements.values()) for (const st of ent.streams) if (st.status === 'locked') st.status = 'claimable';
        this.push('rewards', 'Held-back rewards from closed rounds unlocked for the depositors who earned them.', 'water');
      } else if (eff.type === 'shoeOutcome') {
        this.onShoeOutcome(eff.collected);
        const enc = encounters.find((x) => x.objectIndex === eff.objectIndex);
        if (enc && this.shoe) enc.amount = this.shoe.amount;
      } else if (eff.type === 'forgeAuthorize') {
        const amt = eff.maxAmount < this.forgeReserve ? eff.maxAmount : this.forgeReserve;
        if (amt > 0n) {
          this.forgeReserve -= amt;
          this.authorized += amt;
          this.totals.forgeAuthorized += amt;
          this.forgeStage = 'authorized';
          this.forgeNote = `Authorized ${fmt(amt)} from the existing Forge reserve.`;
          const enc = encounters.find((x) => x.kind === EncounterKind.FORGE_GATE);
          if (enc) enc.amount = amt;
          this.push('forge', `Forge gate: ${fmt(amt)} DEMO-Q cleared to buy back RUNNER (from the Forge reserve, never from depositors).`, 'forge');
        } else this.push('forge', 'Forge gate passed but the Forge reserve is empty: nothing authorized.', 'warn');
      } else if (eff.type === 'finish') {
        this.push('run', `Run ${run.id} finished.`, 'neutral');
      } else if (eff.type === 'closed') {
        rec.open = false;
        rec.road = { ...r.road };
        this.roadReserve += eff.returned;
        this.totals.roadReturned += eff.returned;
        if (eff.returned > 0n) this.ledgerPush({ kind: 'returned', amount: eff.returned, runId: run.id, epoch: this.currentEpoch(), policyId: 1, cohortEpoch: rec.cohortEpoch });
        this.cohortOpenRuns.set(rec.cohortEpoch, Math.max(0, (this.cohortOpenRuns.get(rec.cohortEpoch) ?? 1) - 1));
      }
    }
    rec.road = { ...r.road };
    this.modifierCount = r.road.modifierCount;
    if (r.crossings.some((c) => c.kind === EncounterKind.WATER_STATION && c.hit)) this.push('encounter', 'Water station taken: effects cleared.', 'water');
    this.steps.push({ step: r.stepIndex, window: Number(r.window), net: r.net, volume: r.volume, u: r.u, pace: r.pace, before: r.before, state: r.state, stateHash: r.stateHash, encounters, resolvedAt: this.nowSeconds() });
    if (sim.finished) {
      const reached = r.effects.some((e) => e.type === 'finish');
      run.status = 'finished';
      run.exitRoute = (['neutral', 'forge', 'harvest'] as const)[r.road.exitRoute] ?? 'neutral';
      run.reachedFinish = reached;
      this.stage = 'result';
      this.stageUntilWindow = w + 1 + RESULT_HOLD_WINDOWS;
      this.lastReplayRunId = run.id;
      if (this.shoe && this.shoe.status === 'Open') this.onShoeOutcome(false);
      this.addJournal(
        `Run ${run.id}: ${reached ? `finished on the ${run.exitRoute} route` : 'stopped at the step bound'}`,
        `Of its ${fmt(rec.budget)} DEMO-Q, ${fmt(r.road.toHolders)} went to depositors (round ${rec.cohortEpoch}), ${fmt(r.road.toForge)} to the Forge, and ${fmt(r.road.returned)} back to the reserve for future runs. Fee rewards were never part of the run.`,
        'run',
        [],
      );
    }
  }

  private onShoeOutcome(collected: boolean) {
    const sh = this.shoe;
    if (!sh) return;
    sh.collected = collected;
    const total = sh.entries.length ? sh.entries[sh.entries.length - 1].cumWeight : 0n;
    if (!collected || total === 0n) {
      sh.status = collected ? 'Unawarded' : 'Missed';
      this.prizeReserve += sh.amount;
      this.totals.prizeReturned += sh.amount;
      this.push('prize', collected ? 'Golden Shoe collected but nobody had an eligible entry: prize returns to the reserve.' : 'Golden Shoe missed: the unawarded prize returns to the prize reserve.', 'warn');
      return;
    }
    if (sh.entries.some((e) => e.participant === YOU)) this.achieve('shoe-collected', { runId: this.run?.id ?? null, shoeId: sh.shoeId });
    sh.status = 'Drawing';
    const seed = keccak256(toHex(`runner-demo-${this.sessionSeed}-draw-${sh.shoeId}`));
    const t = ticket(seed, BigInt(sh.shoeId), total);
    const idx = findEntry(sh.entries, t);
    sh.winner = sh.entries[idx].participant;
    sh.status = 'Claimable';
    if (sh.winner === YOU) {
      this.user.prizeOwed += sh.amount;
      this.achieve('shoe-won', { runId: this.run?.id ?? null, shoeId: sh.shoeId });
    }
    const name = this.nameOf(sh.winner);
    this.push('prize', `Golden Shoe grabbed! ${name} wins ${fmt(sh.amount)} DEMO-Q (demo draw).`, 'gold');
    this.addJournal('I grabbed the Golden Shoe', `I reached the shoe. A draw among the buyers who entered picked ${name}, who wins ${fmt(sh.amount)} DEMO-Q.`, 'prize', [`ticket ${t.toString().slice(0, 12)}…`]);
  }

  private advanceForge() {
    if (this.forgeStage === 'authorized' && this.authorized > this.spent) {
      const amount = this.authorized - this.spent;
      const out = buybackOut(this.pool, amount);
      const mid = Number(amount) / price(this.pool);
      if (Number(out) < mid * 0.97) {
        this.forgeNote = 'Conditions poor (impact above bound): purchase left pending.';
        return;
      }
      this.pool.quote += amount;
      this.pool.runner -= out;
      this.spent += amount;
      this.purchased += out;
      this.inventory += out;
      this.forgeStage = 'purchased';
      this.forgeNote = `Bought ${fmtR(out)} RUNNER with ${fmt(amount)} demo quote (no project fee, no steering, no entries).`;
      this.push('forge', `Forge bought back ${fmtR(out)} RUNNER to burn.`, 'forge');
    } else if (this.forgeStage === 'purchased' && this.inventory > 0n) {
      const b = this.inventory;
      this.inventory = 0n;
      this.burned += b;
      this.runnerSupply -= b;
      this.forgeStage = 'burned';
      this.forgeNote = `Burned ${fmtR(b)} RUNNER with the token's burn function; supply is now ${fmtR(this.runnerSupply)}.`;
      this.push('forge', `Burn complete: ${fmtR(b)} RUNNER destroyed.`, 'forge');
      this.addJournal('Forge: bought and burned', `The Forge bought back ${fmtR(b)} RUNNER within its limits and burned it. Demo values.`, 'burn', []);
    }
  }

  // ------------------------------------------------------------------------------------------------
  // Settlement: five-bucket waterfall per closed epoch; holder income by sealed weights
  // ------------------------------------------------------------------------------------------------

  private yourWeight(e: number): bigint {
    return this.lots.filter((l) => l.eligibleFrom <= e).reduce((a, l) => a + l.amount, 0n);
  }

  private settleEpoch(epoch: number) {
    const accrued = this.accrual.get(epoch) ?? 0n;
    this.accrual.delete(epoch);
    const sp = waterfallSplit(accrued, this.policy);
    this.receivable -= accrued;
    this.operations += sp.operations;
    this.prizeReserve += sp.prize;
    this.forgeReserve += sp.forge;
    this.roadReserve += sp.road;
    this.totals.settled += accrued;
    this.totals.toRewards += sp.holdersDirect + sp.holdersDeferred;
    if (sp.road > 0n) this.ledgerPush({ kind: 'settled', amount: sp.road, runId: null, epoch, policyId: 1, cohortEpoch: null });
    this.waterfallEpochs.unshift({
      status: 'settled',
      periodIndex: epoch,
      epoch,
      policyId: 1,
      accrued: s(accrued),
      holdersDirect: s(sp.holdersDirect),
      holdersDeferred: s(sp.holdersDeferred),
      forge: s(sp.forge),
      road: s(sp.road),
      prize: s(sp.prize),
      operations: s(sp.operations),
      txHash: ZERO_HASH,
      blockNumber: '0',
      finality: 'latest',
    });
    if (this.waterfallEpochs.length > 50) this.waterfallEpochs.length = 50;
    // Sealed weights of the completed epoch: deposits held for the whole epoch (eligibleFrom ≤ epoch).
    const you = this.yourWeight(epoch);
    const global = DEMO_HOLDERS + you;
    const idTokens = [...this.identities.values()].filter((t) => t.status === 'live' && t.eligibleFrom <= epoch).map((t) => t.id);
    this.sealed.set(epoch, { you, global, idTokens });
    this.finalizedEpochs = epoch + 1;
    if (global > 0n && sp.holdersDirect + sp.holdersDeferred > 0n) {
      this.user.claimableDirect += (sp.holdersDirect * you) / global;
      this.user.lockedDeferred += (sp.holdersDeferred * you) / global;
    }
    if (you > 0n) {
      const runners = this.lots.filter((l) => l.tokenId !== null && l.eligibleFrom <= epoch).map((l) => ({ tokenId: l.tokenId!, backing: l.amount }));
      const row: DemoEntitlement = { epoch, weight: you, global, runners, streams: [] };
      if (global > 0n && sp.holdersDirect + sp.holdersDeferred > 0n) {
        row.streams.push({ stream: 'direct', credited: sp.holdersDirect, amount: (sp.holdersDirect * you) / global, status: 'claimable' });
        row.streams.push({ stream: 'deferred', credited: sp.holdersDeferred, amount: (sp.holdersDeferred * you) / global, status: 'locked' });
      }
      this.entitlements.set(epoch, row);
    }
    if (accrued > 0n) this.push('rewards', `Round ${epoch} paid out: ${fmt(sp.holdersDirect + sp.holdersDeferred)} to depositors, ${fmt(sp.forge)} Forge, ${fmt(sp.road)} road, ${fmt(sp.prize)} prizes, ${fmt(sp.operations)} operations.`, 'harvest');
  }

  /** Run-cohort stream: a cohort is credited once every run bound to it closed and epoch cohort + 1 has ended. */
  private creditCohorts(currentEpoch: number) {
    for (const [c, amt] of [...this.cohortPending.entries()].sort((a, b) => a[0] - b[0])) {
      if ((this.cohortOpenRuns.get(c) ?? 0) > 0 || currentEpoch < c + 2) break;
      const sw = this.sealed.get(c) ?? { you: 0n, global: DEMO_HOLDERS, idTokens: [] };
      if (sw.global > 0n) this.user.claimableRun += (amt * sw.you) / sw.global;
      const ent = this.entitlements.get(c);
      if (ent && sw.global > 0n && amt > 0n) ent.streams.push({ stream: 'run', credited: amt, amount: (amt * sw.you) / sw.global, status: 'claimable' });
      this.totals.toRunCohorts += amt;
      this.cohortPending.delete(c);
      this.runFinalizedCount = c + 1;
      if (amt > 0n) this.push('rewards', `Run bonus for round ${c}: ${fmt(amt)} DEMO-Q to that round's depositors.`, 'harvest');
    }
  }

  // ------------------------------------------------------------------------------------------------
  // Trading (demo)
  // ------------------------------------------------------------------------------------------------

  private recordInput(runnerDelta: bigint, yours: boolean) {
    const w = this.currentWindow();
    const cur = this.windowNet.get(w) ?? { net: 0n, volume: 0n, swaps: 0, yours: 0n };
    cur.net += runnerDelta;
    cur.volume += runnerDelta < 0n ? -runnerDelta : runnerDelta;
    cur.swaps += 1;
    if (yours) cur.yours += runnerDelta;
    this.windowNet.set(w, cur);
  }

  private accrue(fee: bigint) {
    const e = this.currentEpoch();
    this.accrual.set(e, (this.accrual.get(e) ?? 0n) + fee);
    this.receivable += fee;
    this.totals.accrued += fee;
  }

  private entry(participant: Hex, weight: bigint) {
    const sh = this.shoe;
    if (!sh || sh.status !== 'Open' || this.nowSeconds() >= sh.cutoff || weight === 0n) return;
    const total = (sh.entries.length ? sh.entries[sh.entries.length - 1].cumWeight : 0n) + weight;
    const last = sh.entries[sh.entries.length - 1];
    if (last && last.participant === participant) last.cumWeight = total;
    else sh.entries.push({ participant, cumWeight: total });
  }

  private crowdTrades(w: number) {
    const r = this.rand;
    if (this.stage === 'active' && this.sim && this.run && r() < 0.8) {
      const u = chooseInput(this.sim.state, this.sim.course, this.sim.cursor, REF_SUPPLY, this.m, this.h, { preferRoute: r() < 0.5 ? 1 : 2, collectShoe: true, collectWater: true, passForgeGate: r() < 0.5 }, 2);
      const net = netForInput(u, REF_SUPPLY, this.m) - (this.windowNet.get(w)?.yours ?? 0n);
      const pilot = this.bots[4 + Math.floor(r() * 2)];
      if (net > 0n) this.botBuyRunner(pilot, net);
      else if (net < 0n) this.botSell(pilot, -net);
    }
    const n = 1 + Math.floor(r() * 3);
    for (let i = 0; i < n; i++) {
      const b = this.bots[Math.floor(r() * 4)];
      const buy = b.style === 'buyer' ? r() < 0.75 : b.style === 'seller' ? r() < 0.25 : r() < 0.5;
      if (buy) this.botBuyQuote(b, BigInt(Math.floor(5 + r() * 60)) * E18);
      else this.botSell(b, BigInt(Math.floor(50_000 + r() * 600_000)) * E18);
    }
  }

  private botBuyQuote(b: Bot, q: bigint) {
    const res = quoteBuy(this.pool, q);
    applyBuy(this.pool, res);
    this.accrue(res.projectFee);
    this.recordInput(res.runnerDelta, false);
    this.entry(b.address, res.projectFee);
  }

  private botBuyRunner(b: Bot, runnerOut: bigint) {
    const px = price(this.pool);
    let q = BigInt(Math.ceil((Number(runnerOut) * px) / (1 - 0.008)));
    let res = quoteBuy(this.pool, q);
    for (let i = 0; i < 6 && res.amountOut < runnerOut; i++) {
      q = (q * 1005n) / 1000n;
      res = quoteBuy(this.pool, q);
    }
    applyBuy(this.pool, res);
    this.accrue(res.projectFee);
    this.recordInput(res.runnerDelta, false);
    this.entry(b.address, res.projectFee);
  }

  private botSell(_b: Bot, runnerIn: bigint) {
    const res = quoteSell(this.pool, runnerIn);
    applySell(this.pool, res);
    this.accrue(res.projectFee);
    this.recordInput(res.runnerDelta, false);
  }

  private play: PlaySessionView | null = null;
  private playLastWindow = -1;
  private playBusy = false;
  async steeringConfig() { return { version: 1, engineVersion: 1, movement: this.m, handlers: this.h }; }
  async playSupport() { return { supported: true, reason: 'Simulated session — no wallet or real money.' }; }
  async startPlay(l: PlayLimits): Promise<PlaySessionView> {
    validateLimits(l);
    if (l.buyBudget > this.user.quote || l.sellBudget > this.user.runner) throw new Error('The session exceeds your demo balance.');
    this.play = { id: (this.play?.id ?? 0n) + 1n, expiresAt: this.nowSeconds() + l.duration, buyRemaining: l.buyBudget, sellRemaining: l.sellBudget, maxBuy: l.maxBuy, maxSell: l.maxSell, active: true };
    this.playLastWindow = -1;
    return { ...this.play };
  }
  async playStatus() {
    if (this.play && this.nowSeconds() >= this.play.expiresAt) this.play.active = false;
    return this.play ? { ...this.play } : null;
  }
  async stopPlay() { if (this.play) this.play.active = false; }
  async playTrade(req: TradeRequest, q: QuoteView, window: number): Promise<TxView> {
    const s = await this.playStatus();
    if (!s?.active || this.playBusy || !steeringReady(this.getView()) || window !== this.getView().openInput?.window || window === this.playLastWindow) throw new Error('Session or steering window is no longer available.');
    const buy = req.side === 'buy';
    if (req.amountIn <= 0n || req.amountIn > (buy ? s.maxBuy : s.maxSell) || req.amountIn > (buy ? s.buyRemaining : s.sellRemaining) || q.amountIn !== req.amountIn || q.side !== req.side) throw new Error('Trade exceeds the session permission.');
    this.playBusy = true;
    this.playLastWindow = window;
    try {
      const tx = await this.trade(req, q);
      if (tx.stage === 'confirmed' && this.play?.id === s.id) {
        if (buy) this.play.buyRemaining -= req.amountIn; else this.play.sellRemaining -= req.amountIn;
      }
      return tx;
    } finally { this.playBusy = false; }
  }

  async quote(req: TradeRequest): Promise<QuoteView> {
    const res = req.side === 'buy' ? quoteBuy(this.pool, req.amountIn) : quoteSell(this.pool, req.amountIn);
    const slippageBps = 100;
    return {
      side: req.side,
      amountIn: res.amountIn,
      amountOut: res.amountOut,
      projectFee: res.projectFee,
      lpFee: res.lpFee,
      priceImpactBps: res.priceImpactBps,
      minOut: (res.amountOut * BigInt(10_000 - slippageBps)) / 10_000n,
      slippageBps,
      expiresAt: this.nowSeconds() + 15,
      steersLabel: req.side === 'buy' ? 'Pushes RUNNER right (toward Harvest)' : 'Pushes RUNNER left (toward Forge)',
      shoeEligible: req.side === 'buy' && !!this.shoe && this.shoe.status === 'Open' && this.nowSeconds() < this.shoe.cutoff,
    };
  }

  private tx(label: string, stage: TxView['stage'], detail?: string): TxView {
    return { id: `demo-${++this.feedSeq}`, label, stage, detail, at: this.nowSeconds() };
  }

  async trade(req: TradeRequest, q: QuoteView): Promise<TxView> {
    if ((req.steeringWindow !== undefined && req.steeringWindow !== this.getView().openInput?.window) || (req.validUntil !== undefined && req.validUntil <= this.nowSeconds()) || q.expiresAt < this.nowSeconds()) return this.tx('Demo trade', 'expired', 'Quote expired; request a new quote.');
    if (req.side === 'buy') {
      if (req.amountIn > this.user.quote) return this.tx('Demo buy', 'failed', 'Insufficient demo quote balance.');
      const res = quoteBuy(this.pool, req.amountIn);
      if (res.amountOut < q.minOut) return this.tx('Demo buy', 'failed', 'Output below minimum (price moved).');
      applyBuy(this.pool, res);
      this.user.quote -= res.amountIn;
      this.user.runner += res.amountOut;
      this.accrue(res.projectFee);
      this.recordInput(res.runnerDelta, true);
      this.entry(YOU, res.projectFee);
      this.push('trade', `You bought ${fmtR(res.amountOut)} RUNNER (demo). Fee ${fmt(res.projectFee)} → pushes right.`, 'harvest');
    } else {
      if (req.amountIn > this.user.runner) return this.tx('Demo sell', 'failed', 'Insufficient demo RUNNER balance.');
      const res = quoteSell(this.pool, req.amountIn);
      if (res.amountOut < q.minOut) return this.tx('Demo sell', 'failed', 'Output below minimum (price moved).');
      applySell(this.pool, res);
      this.user.runner -= res.amountIn;
      this.user.quote += res.amountOut;
      this.accrue(res.projectFee);
      this.recordInput(res.runnerDelta, true);
      this.push('trade', `You sold ${fmtR(res.amountIn)} RUNNER (demo). Fee ${fmt(res.projectFee)} → pushes left.`, 'forge');
    }
    this.emit();
    return this.tx(req.side === 'buy' ? 'Demo buy' : 'Demo sell', 'confirmed', 'Simulated instantly — no transaction.');
  }

  /** Free practice input. Replaces the current steering window without changing wallet balances. */
  steerPractice(direction: -1 | 0 | 1) {
    if (this.stage !== 'active') return;
    const net = netForInput(BigInt(direction) * this.m.uMax, REF_SUPPLY, this.m);
    this.windowNet.set(this.currentWindow(), { net, volume: net < 0n ? -net : net, swaps: 0, yours: net });
    this.emit();
  }

  async quickTrade(side: 'buy' | 'sell', size: 'S' | 'M' | 'L' = 'M') {
    const mult = size === 'S' ? 1n : size === 'L' ? 6n : 3n;
    const amountIn = side === 'buy' ? 10n * mult * E18 : 120_000n * mult * E18;
    const q = await this.quote({ side, amountIn });
    return this.trade({ side, amountIn }, q);
  }

  // ------------------------------------------------------------------------------------------------
  // Rewards (demo): one ledger — ordinary and identity-backed principal, three claim streams
  // ------------------------------------------------------------------------------------------------

  async deposit(amount: bigint): Promise<TxView> {
    if (amount <= 0n || amount > this.user.runner) return this.tx('Demo deposit', 'failed', 'Insufficient demo RUNNER.');
    this.user.runner -= amount;
    this.lots.push({ amount, eligibleFrom: this.currentEpoch() + 1, tokenId: null });
    this.push('rewards', `You deposited ${fmtR(amount)} RUNNER (demo). It starts earning in round ${this.currentEpoch() + 1}.`, 'harvest');
    this.emit();
    return this.tx('Demo deposit', 'confirmed');
  }

  /** Ordinary principal only (Runner backing leaves by redemption or conversion back). Pending first, then newest. */
  async withdraw(amount: bigint): Promise<TxView> {
    const ordinary = this.lots.filter((l) => l.tokenId === null);
    const total = ordinary.reduce((a, l) => a + l.amount, 0n);
    if (amount <= 0n || amount > total) return this.tx('Demo withdraw', 'failed', 'More than your ordinary deposit (Runner backing is redeemed from My Runners).');
    let left = amount;
    for (const l of [...ordinary].sort((a, b) => b.eligibleFrom - a.eligibleFrom)) {
      if (left === 0n) break;
      const take = l.amount < left ? l.amount : left;
      l.amount -= take;
      left -= take;
    }
    this.lots = this.lots.filter((l) => l.amount > 0n);
    this.user.runner += amount;
    this.push('rewards', `You withdrew ${fmtR(amount)} RUNNER (demo). Earlier earnings stay claimable.`, 'neutral');
    this.emit();
    return this.tx('Demo withdraw', 'confirmed');
  }

  async claimRewards(): Promise<TxView> {
    const u = this.user;
    const amt = u.claimableDirect + u.claimableDeferred + u.claimableRun;
    if (amt === 0n) return this.tx('Demo claim', 'failed', 'Nothing claimable yet.');
    this.claimsLog.unshift({ direct: s(u.claimableDirect), deferred: s(u.claimableDeferred), run: s(u.claimableRun), to: YOU, failed: false, txHash: ZERO_HASH, blockNumber: '0', at: Math.floor(this.nowSeconds()) });
    u.quote += amt;
    u.claimableDirect = 0n;
    u.claimableDeferred = 0n;
    u.claimableRun = 0n;
    for (const ent of this.entitlements.values()) for (const st of ent.streams) if (st.status === 'claimable') st.status = 'claimed';
    this.push('rewards', `You claimed ${fmt(amt)} DEMO-Q in rewards.`, 'harvest');
    this.emit();
    return this.tx('Demo claim', 'confirmed');
  }

  async claimPrize(): Promise<TxView> {
    const amt = this.user.prizeOwed;
    if (amt === 0n) return this.tx('Demo prize claim', 'failed', 'No prize owed.');
    this.user.prizeOwed = 0n;
    this.user.prizePaid += amt;
    this.user.quote += amt;
    if (this.shoe) this.shoe.paid = true;
    this.push('prize', `Prize paid: ${fmt(amt)} DEMO-Q.`, 'gold');
    this.emit();
    return this.tx('Demo prize claim', 'confirmed');
  }

  async connect() {}
  disconnect() {}

  // ------------------------------------------------------------------------------------------------
  // Runner identities (demo): same rules as RunnerRewards + RunnerIdentity
  // ------------------------------------------------------------------------------------------------

  private isYou(a: string | null): boolean {
    return !!a && a.toLowerCase() === YOU.toLowerCase();
  }

  private mintFor(owner: Hex, origin: 'deposit' | 'convert', eligibleFrom: number): DemoIdentity {
    const id = ++this.minted;
    const e = this.currentEpoch();
    const t: DemoIdentity = {
      id,
      owner,
      backing: this.backing,
      eligibleFrom,
      mintedEpoch: e,
      mintedAt: Math.floor(this.nowSeconds()),
      origin,
      status: 'live',
      transfers: 0,
      history: [],
      equipment: new Map(),
    };
    t.history.push(this.histEntry('minted', null, owner, e, { origin, backing: s(this.backing), eligibleFrom }));
    this.identities.set(id, t);
    return t;
  }

  private histEntry(event: IdentityDetail['history'][number]['event'], from: string | null, to: string | null, epoch: number, detail: Record<string, unknown>): IdentityDetail['history'][number] {
    return { event, from: (from as Hex) ?? null, to: (to as Hex) ?? null, epoch, detail, txHash: ZERO_HASH, blockNumber: '0', at: Math.floor(this.nowSeconds()), finality: 'latest' };
  }

  private intakeBlock(): string | null {
    if (this.minted >= this.cap) return 'Collection cap reached: no new Runner IDs can be issued.';
    return null;
  }

  async mintIdentity(): Promise<TxView> {
    const label = 'Mint a Runner (deposit B, demo)';
    const blocked = this.intakeBlock();
    if (blocked) return this.tx(label, 'failed', blocked);
    if (this.user.runner < this.backing) return this.tx(label, 'failed', `Minting deposits exactly B = ${fmtR(this.backing)} RUNNER; your demo wallet holds ${fmtR(this.user.runner)}.`);
    this.user.runner -= this.backing;
    const t = this.mintFor(YOU, 'deposit', this.currentEpoch() + 1);
    this.lots.push({ amount: this.backing, eligibleFrom: t.eligibleFrom, tokenId: t.id });
    this.push('identity', `You minted Runner #${t.id} by depositing ${fmtR(this.backing)} RUNNER (demo). It starts earning in round ${t.eligibleFrom}.`, 'harvest');
    this.emit();
    return this.tx(label, 'confirmed', `Runner #${t.id}`);
  }

  /** Turn B of the ordinary position into a Runner. B must be entirely mature or entirely pending (MixedMaturity). */
  async convertToIdentity(): Promise<TxView> {
    const label = 'Convert B of your deposit into a Runner (demo)';
    const blocked = this.intakeBlock();
    if (blocked) return this.tx(label, 'failed', blocked);
    const e = this.currentEpoch();
    const ordinary = this.lots.filter((l) => l.tokenId === null);
    const mature = ordinary.filter((l) => l.eligibleFrom <= e);
    const pending = ordinary.filter((l) => l.eligibleFrom > e);
    const sum = (ls: Lot[]) => ls.reduce((a, l) => a + l.amount, 0n);
    const source = sum(mature) >= this.backing ? mature : sum(pending) >= this.backing ? pending : null;
    if (!source) {
      return this.tx(
        label,
        'failed',
        sum(ordinary) >= this.backing
          ? 'MixedMaturity: B would straddle mature and pending principal. Wait until the pending part matures.'
          : `Your ordinary deposit (${fmtR(sum(ordinary))}) is below B = ${fmtR(this.backing)} RUNNER.`,
      );
    }
    let left = this.backing;
    const eligibleFrom = source === mature ? Math.min(...mature.map((l) => l.eligibleFrom)) : Math.max(...pending.map((l) => l.eligibleFrom));
    for (const l of source) {
      if (left === 0n) break;
      const take = l.amount < left ? l.amount : left;
      l.amount -= take;
      left -= take;
    }
    this.lots = this.lots.filter((l) => l.amount > 0n);
    const t = this.mintFor(YOU, 'convert', eligibleFrom);
    this.lots.push({ amount: this.backing, eligibleFrom, tokenId: t.id });
    this.push('identity', `You turned ${fmtR(this.backing)} RUNNER of your deposit into Runner #${t.id} (demo). Your earnings carry on unchanged.`, 'harvest');
    this.emit();
    return this.tx(label, 'confirmed', `Runner #${t.id}`);
  }

  private ownedLive(tokenId: bigint): DemoIdentity | string {
    const t = this.identities.get(Number(tokenId));
    if (!t || t.status !== 'live') return `Runner #${tokenId} is not live.`;
    if (!this.isYou(t.owner)) return 'NotOwner: only the current owner can do this (an approved operator cannot redeem).';
    return t;
  }

  async convertToOrdinary(tokenId: bigint): Promise<TxView> {
    const label = `Convert Runner #${tokenId} back to an ordinary deposit (demo)`;
    const t = this.ownedLive(tokenId);
    if (typeof t === 'string') return this.tx(label, 'failed', t);
    t.status = 'converted';
    t.history.push(this.histEntry('converted', YOU, null, this.currentEpoch(), { to: 'ordinary' }));
    const lot = this.lots.find((l) => l.tokenId === t.id);
    if (lot) lot.tokenId = null;
    t.owner = null;
    this.push('identity', `Runner #${t.id} converted back: its ${fmtR(t.backing)} RUNNER stay deposited as an ordinary position (demo).`, 'neutral');
    this.emit();
    return this.tx(label, 'confirmed');
  }

  async redeemIdentity(tokenId: bigint): Promise<TxView> {
    const label = `Redeem Runner #${tokenId} (demo)`;
    const t = this.ownedLive(tokenId);
    if (typeof t === 'string') return this.tx(label, 'failed', t);
    t.status = 'redeemed';
    t.history.push(this.histEntry('redeemed', YOU, null, this.currentEpoch(), { backing: s(t.backing) }));
    this.lots = this.lots.filter((l) => l.tokenId !== t.id);
    t.owner = null;
    this.user.runner += t.backing;
    this.push('identity', `Runner #${t.id} burned; its ${fmtR(t.backing)} RUNNER backing returned to your wallet. Your earlier claims remain (demo).`, 'neutral');
    this.emit();
    return this.tx(label, 'confirmed');
  }

  async transferIdentity(tokenId: bigint, to: string): Promise<TxView> {
    const label = `Transfer Runner #${tokenId} (demo)`;
    if (!/^0x[0-9a-fA-F]{40}$/.test(to)) return this.tx(label, 'failed', 'Recipient must be a 0x-prefixed 20-byte address.');
    if (this.isYou(to)) return this.tx(label, 'failed', 'That is your own address: a self-transfer changes nothing.');
    const t = this.ownedLive(tokenId);
    if (typeof t === 'string') return this.tx(label, 'failed', t);
    const e = this.currentEpoch();
    // Seller loses the backing from the current epoch; the buyer counts from the next one. Earlier rights stay.
    this.lots = this.lots.filter((l) => l.tokenId !== t.id);
    t.owner = to as Hex;
    t.eligibleFrom = e + 1;
    t.transfers += 1;
    t.history.push(this.histEntry('transferred', YOU, to, e, { eligibleFrom: e + 1 }));
    this.push('identity', `Runner #${t.id} sent to ${to.slice(0, 8)}… (demo). Your earnings before round ${e} stay with you; the new owner earns from round ${e + 1}.`, 'neutral');
    this.emit();
    return this.tx(label, 'confirmed');
  }

  async equip(tokenId: bigint, slot: number, itemId: number): Promise<TxView> {
    const label = `Equip Runner #${tokenId} (demo)`;
    const t = this.ownedLive(tokenId);
    if (typeof t === 'string') return this.tx(label, 'failed', t);
    if (slot < 0 || slot > 3 || (itemId !== 0 && WARDROBE.find((w) => w.itemId === itemId)?.slot !== slot)) return this.tx(label, 'failed', 'BadItem: that item does not belong in this slot.');
    const key = YOU.toLowerCase();
    const eq = t.equipment.get(key) ?? [0, 0, 0, 0];
    eq[slot] = itemId;
    t.equipment.set(key, eq);
    t.history.push(this.histEntry('equipped', YOU, null, this.currentEpoch(), { slot, itemId }));
    this.push('identity', itemId ? `Runner #${t.id}: ${WARDROBE.find((w) => w.itemId === itemId)?.name} equipped (cosmetic only).` : `Runner #${t.id}: slot cleared.`, 'neutral');
    this.emit();
    return this.tx(label, 'confirmed', 'Cosmetic: weight, steering, hitboxes, odds and backing are unchanged.');
  }

  private summary(t: DemoIdentity): IdentitySummary {
    return {
      tokenId: String(t.id),
      status: t.status,
      owner: t.owner,
      backing: s(t.backing),
      eligibleFrom: t.eligibleFrom,
      origin: t.origin,
      mintedAt: t.mintedAt,
      mintedBlock: '0',
      transfers: t.transfers,
      traits: traitLabels(baseTraits(IDENTITY_TRAIT_SEED, BigInt(t.id))),
    };
  }

  async identity(tokenId: bigint): Promise<IdentityDetail> {
    const t = this.identities.get(Number(tokenId));
    if (!t) throw new Error(`Runner #${tokenId} was never minted`);
    const traits = baseTraits(IDENTITY_TRAIT_SEED, tokenId);
    const labels = traitLabels(traits);
    const e = this.currentEpoch();
    const eq = t.owner ? (t.equipment.get(t.owner.toLowerCase()) ?? [0, 0, 0, 0]) : [0, 0, 0, 0];
    const meta = t.status === 'live'
      ? {
          name: `Runner #${t.id}`,
          description: `A RUNNER identity backed by ${fmtR(t.backing)} RUNNER held in the rewards vault. Redeemable by its owner for that backing. Not a yield multiplier.`,
          image: svgDataUri(identitySvg(IDENTITY_TRAIT_SEED, tokenId)),
          attributes: [
            ...TRAIT_TITLES.map(([k, title]) => [title, labels[k]]),
            ['Backing (RUNNER)', fmtR(t.backing).replace(/,/g, '')],
            ['Renderer', 'base v1'],
          ].map(([trait_type, value]) => ({ trait_type, value })),
        }
      : null;
    // Holdings: one segment per owner (eligibleFrom → transfer epoch).
    const holdings: IdentityDetail['holdings'] = [];
    for (const h of t.history) {
      if (h.event === 'minted' || h.event === 'transferred') {
        const prev = holdings[holdings.length - 1];
        if (prev && prev.untilEpoch === null) prev.untilEpoch = h.epoch;
        holdings.push({ owner: h.to as Hex, eligibleFrom: Number(h.detail.eligibleFrom ?? (h.epoch ?? 0) + 1), untilEpoch: null });
      } else if (h.event === 'redeemed' || h.event === 'converted') {
        const prev = holdings[holdings.length - 1];
        if (prev && prev.untilEpoch === null) prev.untilEpoch = h.epoch;
      }
    }
    const participation: IdentityDetail['participation'] = [];
    for (const r of this.runs.values()) {
      const seg = holdings.find((h) => h.eligibleFrom <= r.cohortEpoch && (h.untilEpoch === null || r.cohortEpoch < h.untilEpoch));
      if (seg && this.sealed.get(r.cohortEpoch)?.idTokens.includes(t.id)) participation.push({ runId: r.id, cohortEpoch: r.cohortEpoch, owner: seg.owner });
    }
    return {
      tokenId: String(t.id),
      status: t.status,
      owner: t.owner,
      backing: s(t.backing),
      eligibleFrom: t.eligibleFrom,
      mintedEpoch: t.mintedEpoch,
      eligibleNow: t.status === 'live' && t.eligibleFrom <= e,
      origin: t.origin,
      traits: { values: [...traits], labels, seed: IDENTITY_TRAIT_SEED, verifiedOnchain: null },
      metadataUri: meta ? jsonDataUri(meta) : null,
      renderer: { cosmetic: null, base: ZERO_ADDR },
      equipment: {
        owner: t.owner,
        slots: eq.map((itemId, slot) => {
          const it = WARDROBE.find((w) => w.itemId === itemId);
          return { slot, slotName: WARDROBE_SLOTS[slot], item: it ? { ...it, artHash: ZERO_HASH } : null };
        }),
      },
      history: [...t.history],
      holdings,
      participation,
    };
  }

  async achievements(account: string): Promise<Achievements> {
    const mine = this.isYou(account) ? this.achievementsLog : [];
    const count = (k: string) => mine.filter((a) => a.kind === k).length;
    return {
      account: account as Hex,
      summary: { runsWithRunner: count('runner-in-cohort'), runsSteered: count('run-steered'), forgeGatesPassed: count('forge-gate'), shoesCollected: count('shoe-collected'), shoesWon: count('shoe-won') },
      achievements: [...mine],
      nextCursor: null,
      definitions: {
        'runner-in-cohort': "Held a Runner whose backing was counted in a run's sealed holder cohort (one per run and token).",
        'run-steered': 'Traded in at least one steering window of the run.',
        'forge-gate': 'Traded in the control window of a step whose Forge gate was hit.',
        'shoe-collected': 'Had an entry in a Golden Shoe the runner collected.',
        'shoe-won': 'Won a Golden Shoe draw.',
      },
    };
  }

  private achieve(kind: Achievements['achievements'][number]['kind'], x: { runId?: number | null; tokenId?: number; shoeId?: number; step?: number }) {
    this.achievementsLog.unshift({
      kind,
      runId: x.runId ?? null,
      tokenId: x.tokenId !== undefined ? String(x.tokenId) : null,
      shoeId: x.shoeId !== undefined ? String(x.shoeId) : null,
      step: x.step ?? null,
      txHash: ZERO_HASH,
      blockNumber: '0',
      at: Math.floor(this.nowSeconds()),
      finality: 'latest',
    });
  }

  // ------------------------------------------------------------------------------------------------
  // Operator preview (demo: computed in this browser, labelled)
  // ------------------------------------------------------------------------------------------------

  operatorSession(): OperatorSession | null {
    return null;
  }

  async simulateWaterfall(req: WaterfallSimRequest): Promise<WaterfallSimulation> {
    const revenue = BigInt(req.revenue);
    const capBps = (this.road.overheatCap * 10_000n) / this.road.runBudget;
    const out = previewWaterfall(
      revenue,
      this.policy,
      req.run ? { ...req.run, waterSliceBps: this.road.waterSliceBps, overheatSliceBps: this.road.overheatSliceBps, overheatCapBps: capBps } : null,
    );
    const p = this.policy;
    return {
      previewOnly: true,
      profile: 'WATERFALL_V1 (demo: computed in your browser with the WaterfallLib mirror)',
      policy: { policyId: 1, fromEpoch: 0, holdersBps: Number(p.holdersBps), forgeBps: Number(p.forgeBps), roadBps: Number(p.roadBps), prizeBps: Number(p.prizeBps), opsBps: Number(p.opsBps), holderDeferredBps: Number(p.holderDeferredBps) },
      revenue: s(revenue),
      split: Object.fromEntries(Object.entries(out.split).map(([k, v]) => [k, s(v)])) as WaterfallSimulation['split'],
      run: out.run
        ? { budget: s(out.run.budget), water: s(out.run.water), overheat: s(out.run.overheat), exitToHolders: s(out.run.exitToHolders), exitToForge: s(out.run.exitToForge), returnedToRoad: s(out.run.returnedToRoad), roadNotReserved: '0' }
        : null,
      outcome: Object.fromEntries(Object.entries(out.outcome).map(([k, v]) => [k, s(v)])) as WaterfallSimulation['outcome'],
      legacy: null,
    };
  }

  // ------------------------------------------------------------------------------------------------
  // View
  // ------------------------------------------------------------------------------------------------

  private nameOf(a: Hex | null): string {
    if (!a) return '—';
    if (a === YOU) return 'You';
    return this.bots.find((b) => b.address === a)?.name ?? `${a.slice(0, 6)}…`;
  }

  private push(kind: FeedItem['kind'], text: string, tone: FeedItem['tone'] = 'neutral') {
    this.feed.unshift({ id: `f${++this.feedSeq}`, at: this.nowSeconds(), kind, text, tone });
    if (this.feed.length > 80) this.feed.length = 80;
  }

  private addJournal(title: string, body: string, kind: string, evidence: string[]) {
    this.journal.unshift({ id: `j${this.journal.length + 1}`, at: this.nowSeconds(), title, body, kind, evidence, label: 'DEMO entry — simulated events' });
  }

  private ledgerPush(e: Omit<TreasurySnapshot['road']['ledger'][number], 'amount' | 'from' | 'txHash' | 'blockNumber' | 'at' | 'finality'> & { amount: bigint }) {
    this.ledger.unshift({ ...e, amount: s(e.amount), from: null, txHash: ZERO_HASH, blockNumber: '0', at: Math.floor(this.nowSeconds()), finality: 'latest' });
    if (this.ledger.length > 50) this.ledger.length = 50;
  }

  phaseStage(): { phase: Phase; stage: Stage; detail: string } {
    const fundable = this.fundableNow() > 0n;
    switch (this.stage) {
      case 'funding':
        return { phase: 'funding', stage: 'awaiting-randomness', detail: 'randomness-pending' };
      case 'preview':
        return { phase: 'preview', stage: 'preview', detail: 'course-revealed-first-window-not-open' };
      case 'active':
        return { phase: 'running', stage: 'input-open', detail: `window-${this.currentWindow()}-feeds-step` };
      case 'result':
        return { phase: 'finished', stage: 'result', detail: 'run-result' };
      default:
        if (fundable && this.lastReplayRunId !== null) return { phase: 'replay', stage: 'awaiting-next-run', detail: 'fundable-awaiting-course-request' };
        return {
          phase: 'warm-up',
          stage: this.runId === 0 ? 'no-run' : 'awaiting-next-run',
          detail: fundable ? 'fundable-awaiting-course-request' : this.operations < this.road.opsProvision ? 'operations-below-provision' : 'road-reserve-below-minimum',
        };
    }
  }

  private fundingReason(): 'fundable' | 'road-reserve-below-minimum' | 'operations-below-provision' {
    if (this.fundableNow() > 0n) return 'fundable';
    return this.operations < this.road.opsProvision ? 'operations-below-provision' : 'road-reserve-below-minimum';
  }

  private principalNow() {
    const e = this.currentEpoch();
    const sum = (f: (l: Lot) => boolean) => this.lots.filter(f).reduce((a, l) => a + l.amount, 0n);
    const eligible = sum((l) => l.eligibleFrom <= e);
    const pending = sum((l) => l.eligibleFrom > e);
    const idEligible = sum((l) => l.tokenId !== null && l.eligibleFrom <= e);
    const idPending = sum((l) => l.tokenId !== null && l.eligibleFrom > e);
    return { eligible, pending, idEligible, idPending };
  }

  private accountView(): RewardsAccount {
    const p = this.principalNow();
    const u = this.user;
    const e = this.currentEpoch();
    const owned = [...this.identities.values()].filter((t) => t.status === 'live' && this.isYou(t.owner));
    return {
      account: YOU,
      payout: YOU,
      wallet: { runner: s(u.runner), quote: s(u.quote) },
      principal: {
        total: s(p.eligible + p.pending),
        eligibleNow: s(p.eligible),
        pendingNow: s(p.pending),
        ordinary: s(p.eligible + p.pending - p.idEligible - p.idPending),
        ordinaryEligible: s(p.eligible - p.idEligible),
        ordinaryPending: s(p.pending - p.idPending),
        identityBacked: s(p.idEligible + p.idPending),
        identityEligible: s(p.idEligible),
        identityPending: s(p.idPending),
      },
      claimable: { direct: s(u.claimableDirect), deferred: s(u.claimableDeferred), run: s(u.claimableRun), total: s(u.claimableDirect + u.claimableDeferred + u.claimableRun) },
      lockedDeferredEstimate: s(u.lockedDeferred),
      unpaid: '0',
      prizeOwed: s(u.prizeOwed),
      prizePaid: s(u.prizePaid),
      identities: owned.map((t) => ({ tokenId: String(t.id), backing: s(t.backing), eligibleFrom: t.eligibleFrom, eligibleNow: t.eligibleFrom <= e })),
      epochs: { current: e, finalized: this.finalizedEpochs, runFinalized: this.runFinalizedCount, deferredClaimable: this.finalizedEpochs },
      globalWeightCurrentEpoch: s(DEMO_HOLDERS + p.eligible),
      recentClaims: this.claimsLog.slice(0, 10),
    };
  }

  /**
   * DEMO equivalent of GET /v1/rewards/:account/breakdown: the in-browser ledger's entitlements per settled epoch and
   * per Runner you hold or held. Its claimable rows must sum to the demo account's claimable streams (checked here).
   */
  private breakdownView(): RewardsBreakdown {
    const e = this.currentEpoch();
    const u = this.user;
    const rows = [...this.entitlements.values()].filter((r) => r.streams.length > 0).sort((a, b) => b.epoch - a.epoch);
    const total = (status: DemoEntitlementStatus, stream?: DemoStreamName) =>
      rows.reduce((a, r) => a + r.streams.filter((x) => x.status === status && (!stream || x.stream === stream)).reduce((c, x) => c + x.amount, 0n), 0n);
    const totals = (status: DemoEntitlementStatus) => {
      const d = total(status, 'direct');
      const f = total(status, 'deferred');
      const r = total(status, 'run');
      return { direct: s(d), deferred: s(f), run: s(r), total: s(d + f + r) };
    };
    const claimable = { direct: s(u.claimableDirect), deferred: s(u.claimableDeferred), run: s(u.claimableRun), total: s(u.claimableDirect + u.claimableDeferred + u.claimableRun) };
    const fromRows = totals('claimable');
    const matches = fromRows.direct === claimable.direct && fromRows.deferred === claimable.deferred && fromRows.run === claimable.run;
    const share = (amount: bigint, backing: bigint, weight: bigint) => (weight > 0n ? (amount * backing) / weight : 0n);
    // Runners you hold or held: holding periods from their collectible history (the demo account only mints/converts).
    const held = [...this.identities.values()].filter((t) => t.history.some((h) => h.event === 'minted' && this.isYou(h.to)));
    const agg = new Map<number, { direct: bigint; deferred: bigint; run: bigint; claimable: bigint; unclaimed: bigint; epochs: number[] }>();
    for (const r of rows) {
      for (const t of r.runners) {
        const a = agg.get(t.tokenId) ?? { direct: 0n, deferred: 0n, run: 0n, claimable: 0n, unclaimed: 0n, epochs: [] };
        for (const x of r.streams) {
          const part = share(x.amount, t.backing, r.weight);
          a[x.stream] += part;
          if (x.status === 'claimable') a.claimable += part;
          if (x.status !== 'claimed') a.unclaimed += part;
        }
        a.epochs.unshift(r.epoch);
        agg.set(t.tokenId, a);
      }
    }
    const lastEpochOf = (stream: DemoStreamName) => {
      const claimed = rows.filter((r) => r.streams.some((x) => x.stream === stream && x.status === 'claimed')).map((r) => r.epoch);
      return claimed.length ? Math.max(...claimed) + 1 : 0;
    };
    return {
      account: YOU,
      complete: matches,
      reason: matches ? null : 'demo ledger rows do not sum to the demo claimable streams',
      indexedHead: null,
      pinnedTo: 'latest',
      claimable,
      breakdownClaimable: fromRows,
      notYetClaimable: { locked: s(total('locked')), notFinal: s(total('not-final')) },
      claimedRecomputed: totals('claimed'),
      unpaid: '0',
      checks: { indexCoversPinnedBlock: true, segmentsMatch: true, claimableMatches: matches },
      vault: {
        currentEpoch: e,
        finalizedCount: this.finalizedEpochs,
        runFinalizedCount: this.runFinalizedCount,
        deferredClaimableCount: this.finalizedEpochs,
        directNext: lastEpochOf('direct'),
        deferredNext: lastEpochOf('deferred'),
        runNext: lastEpochOf('run'),
      },
      perWeightScale: (10n ** 36n).toString(),
      epochs: rows.slice(0, 100).map((r) => {
        const amount = r.streams.reduce((a, x) => a + x.amount, 0n);
        const runners = r.runners.map((t) => {
          const parts = { direct: 0n, deferred: 0n, run: 0n };
          for (const x of r.streams) parts[x.stream] += share(x.amount, t.backing, r.weight);
          return { tokenId: String(t.tokenId), backing: s(t.backing), direct: s(parts.direct), deferred: s(parts.deferred), run: s(parts.run), amount: s(parts.direct + parts.deferred + parts.run) };
        });
        const idWeight = r.runners.reduce((a, t) => a + t.backing, 0n);
        return {
          epoch: r.epoch,
          weight: s(r.weight),
          identityWeight: s(idWeight),
          ordinaryWeight: s(r.weight - idWeight),
          streams: r.streams.map((x) => ({
            stream: x.stream, credited: s(x.credited), carriedIn: '0', globalWeight: s(r.global), perWeight: s((x.credited * 10n ** 36n) / r.global),
            amount: s(x.amount), status: x.status, creditTx: ZERO_HASH, creditBlock: '0',
          })),
          amount: s(amount),
          unclaimed: s(r.streams.reduce((a, x) => a + (x.status === 'claimed' ? 0n : x.amount), 0n)),
          runners,
          ordinaryAmount: s(amount - runners.reduce((a, x) => a + BigInt(x.amount), 0n)),
        };
      }),
      epochCount: rows.length,
      nextCursor: rows.length > 100 ? rows[99].epoch : null,
      runners: held.map((t) => {
        const minted = t.history.find((h) => h.event === 'minted');
        const end = [...t.history].reverse().find((h) => h.event === 'transferred' || h.event === 'redeemed' || h.event === 'converted');
        const endedBy = end ? (end.event as 'transferred' | 'redeemed' | 'converted') : null;
        const a = agg.get(t.id) ?? { direct: 0n, deferred: 0n, run: 0n, claimable: 0n, unclaimed: 0n, epochs: [] };
        return {
          tokenId: String(t.id),
          backing: s(t.backing),
          status: t.status === 'live' && this.isYou(t.owner) ? ('held' as const) : (endedBy ?? 'transferred'),
          periods: [{ eligibleFrom: Number((minted?.detail as { eligibleFrom?: number } | undefined)?.eligibleFrom ?? t.eligibleFrom), until: end ? end.epoch : null, endedBy }],
          epochs: a.epochs,
          direct: s(a.direct),
          deferred: s(a.deferred),
          run: s(a.run),
          amount: s(a.direct + a.deferred + a.run),
          claimable: s(a.claimable),
          unclaimed: s(a.unclaimed),
        };
      }),
      note: 'DEMO: computed by the in-browser simulation with the vault rules (no chain, no index). Labelled demo values.',
    };
  }

  private runnersView(): AccountRunners {
    const live = [...this.identities.values()];
    const owned = live.filter((t) => t.status === 'live' && this.isYou(t.owner));
    const before = live.filter((t) => !owned.includes(t) && t.history.some((h) => this.isYou(h.to)));
    const p = this.principalNow();
    return {
      account: YOU,
      owned: owned.map((t) => this.summary(t)),
      previouslyOwned: before.map((t) => this.summary(t)),
      identityPrincipal: { eligible: s(p.idEligible), pending: s(p.idPending), total: s(p.idEligible + p.idPending) },
    };
  }

  private treasuryView(): TreasurySnapshot {
    const open = this.run && this.runs.get(this.run.id)?.open ? this.runs.get(this.run.id)! : null;
    const runEscrowed = open ? open.budget - open.road.assigned : 0n;
    const cohortPending = [...this.cohortPending.values()].reduce((a, b) => a + b, 0n);
    const live = [...this.identities.values()].filter((t) => t.status === 'live');
    const idPrincipal = BigInt(live.length) * this.backing;
    const p = this.principalNow();
    const vaultPrincipal = DEMO_HOLDERS + p.eligible + p.pending;
    const accountedCash = this.operations + this.prizeReserve + this.forgeReserve + this.roadReserve + this.executionReserve + runEscrowed + cohortPending;
    const b = (v: string | null) => BigInt(v ?? '0');
    const wt = this.waterfallEpochs.reduce(
      (a, r) => ({
        accrued: a.accrued + BigInt(r.accrued),
        holdersDirect: a.holdersDirect + b(r.holdersDirect),
        holdersDeferred: a.holdersDeferred + b(r.holdersDeferred),
        forge: a.forge + b(r.forge),
        road: a.road + b(r.road),
        prize: a.prize + b(r.prize),
        operations: a.operations + b(r.operations),
      }),
      { accrued: 0n, holdersDirect: 0n, holdersDeferred: 0n, forge: 0n, road: 0n, prize: 0n, operations: 0n },
    );
    const pol = this.policy;
    const t = this.totals;
    const sp = this.sponsorship;
    // Accrual epochs not split yet (the demo settles each epoch when it closes, so normally only the open one).
    const now = this.currentEpoch();
    const unsplit = (status: 'open' | 'closed-unsettled', periodIndex: number | null, epoch: number, accrued: bigint): TreasurySnapshot['waterfall']['epochs'][number] => ({
      status, periodIndex, epoch, policyId: 1, accrued: s(accrued), holdersDirect: null, holdersDeferred: null, forge: null, road: null, prize: null, operations: null,
      txHash: null, blockNumber: '0', finality: 'latest',
    });
    const pendingRows = [...this.accrual.entries()].sort((x, y) => y[0] - x[0]).map(([ep, acc]) => unsplit(ep >= now ? 'open' : 'closed-unsettled', ep, ep, acc));
    if (!this.accrual.has(now)) pendingRows.unshift(unsplit('open', null, now, 0n));
    return {
      quoteSymbol: 'DEMO-Q',
      quoteDecimals: 18,
      buckets: {
        operations: s(this.operations),
        prizeReserve: s(this.prizeReserve),
        forgeReserve: s(this.forgeReserve),
        roadReserve: s(this.roadReserve),
        executionReserve: s(this.executionReserve),
        lpIncome: '0',
        runEscrowed: s(runEscrowed),
        cohortPending: s(cohortPending),
        pendingDirect: '0',
        pendingDeferred: '0',
        holderCreditsQueued: '0',
      },
      receivable: s(this.receivable),
      accountedCash: s(accountedCash),
      balance: s(accountedCash),
      solvent: true,
      holderCredits: { queued: '0', count: 0, oldestEpoch: null, epochs: [] },
      totals: Object.fromEntries(Object.entries(t).map(([k, v]) => [k, s(v)])) as TreasurySnapshot['totals'],
      waterfall: {
        policy: { policyId: 1, fromEpoch: 0, holdersBps: Number(pol.holdersBps), forgeBps: Number(pol.forgeBps), roadBps: Number(pol.roadBps), prizeBps: Number(pol.prizeBps), opsBps: Number(pol.opsBps), holderDeferredBps: Number(pol.holderDeferredBps) },
        totals: { ...(Object.fromEntries(Object.entries(wt).map(([k, v]) => [k, s(v)])) as Record<keyof typeof wt, string>), epochs: this.waterfallEpochs.length },
        epochs: [...pendingRows, ...this.waterfallEpochs],
        unsettled: {
          periods: this.accrual.size,
          closedPeriods: [...this.accrual.keys()].filter((ep) => ep < now).length,
          listed: this.accrual.size,
          accrued: s(this.receivable),
        },
      },
      road: {
        reserve: s(this.roadReserve),
        runEscrowed: s(runEscrowed),
        executionReserve: s(this.executionReserve),
        openRun: open ? { runId: open.id, ...this.escrowDto(open) } : null,
        cohort: { active: this.cohortPending.size > 0, epoch: [...this.cohortPending.keys()].sort((a, b) => a - b)[0] ?? 0, openRuns: open ? 1 : 0, pending: s(cohortPending) },
        runFinalizedCount: this.runFinalizedCount,
        ledger: this.ledger,
      },
      sponsorship: {
        total: s(sp.prize + sp.forge + sp.road + sp.operations),
        byBucket: { prize: s(sp.prize), forge: s(sp.forge), road: s(sp.road), operations: s(sp.operations) },
        note: 'Demo sponsorship (simulated, labelled): recorded per bucket and never reported as fee revenue.',
      },
      identity: {
        backing: s(this.backing),
        cap: this.cap,
        minted: this.minted,
        live: live.length,
        principalLiability: s(idPrincipal),
        expectedLiability: s(idPrincipal),
        consistent: true,
        vaultPrincipal: s(vaultPrincipal),
        ordinaryPrincipal: s(vaultPrincipal - idPrincipal),
        strayRunner: '0',
      },
      finalizedEpochs: this.finalizedEpochs,
      nextPeriod: this.finalizedEpochs,
      currentPeriod: this.currentEpoch(),
      custody: [],
      note: 'Demo values. Reserves, escrows and identity backing are accounting obligations — not a price floor, a redemption price for RUNNER or a promised yield.',
    };
  }

  private escrowDto(r: DemoRunRecord) {
    return {
      open: r.open,
      cohortEpoch: r.cohortEpoch,
      policyId: 1,
      budget: s(r.budget),
      assigned: s(r.road.assigned),
      toHolders: s(r.road.toHolders),
      toForge: s(r.road.toForge),
      unassigned: s(r.open ? r.budget - r.road.assigned : 0n),
      returnedToRoad: r.open ? null : s(r.budget - r.road.assigned),
      provision: s(r.provision),
    };
  }

  private versionsView(): VersionsView {
    const pol = this.policy;
    const policy = { policyId: 1, fromEpoch: 0, holdersBps: Number(pol.holdersBps), forgeBps: Number(pol.forgeBps), roadBps: Number(pol.roadBps), prizeBps: Number(pol.prizeBps), opsBps: Number(pol.opsBps), holderDeferredBps: Number(pol.holderDeferredBps) };
    return {
      currentEpoch: this.currentEpoch(),
      engines: [
        { engineId: 1, address: ZERO_ADDR, version: 1, codeHash: ZERO_HASH, registeredAt: 0, txHash: null, configs: [1] },
        { engineId: 2, address: ZERO_ADDR, version: 2, codeHash: ZERO_HASH, registeredAt: 0, txHash: null, configs: [] },
      ],
      configs: { active: 1, count: 1, items: [{ version: 1, engineId: 1, engineVersion: 1, engineAddress: ZERO_ADDR, hash: null, active: true, registeredAt: 0, activatedAt: 0, previewWindows: 2, formations: [1, 2, 3] }] },
      policies: { current: { ...policy, status: 'current', scheduledAt: 0, txHash: null, replaced: [] }, scheduled: [], historical: [] },
      releases: {
        economic: [],
        cosmetic: WARDROBE.map((w, i) => ({
          id: i + 1,
          kind: 'RegisterCosmeticItem',
          status: 'Active',
          proposer: ZERO_ADDR,
          approvals: 1,
          createdAt: 0,
          eta: 60,
          executedAt: 60,
          payloadHash: ZERO_HASH,
          rationaleHash: ZERO_HASH,
          simulationHash: ZERO_HASH,
          uri: `demo://wardrobe/${w.itemId}`,
          result: String(w.itemId),
          class: 'cosmetic' as const,
        })),
        record: [],
      },
      governance: { delay: Number(DEFAULTS.govMinDelayLocal), minDelay: Number(DEFAULTS.govMinDelayLocal), note: 'Demo mirrors the local deployment: every proposal kind waits the same delay; there is no shorter cosmetic policy. Proposed production minimum: 24 hours.' },
      cosmetics: { renderer: null, baseRenderer: ZERO_ADDR, items: WARDROBE.map((w) => ({ ...w, artHash: ZERO_HASH })) },
    };
  }

  getView(): WorldView {
    const w = this.currentWindow();
    const now = this.nowSeconds();
    const sh = this.shoe;
    const run = this.run ? { ...this.run } : null;
    if (run && sh) {
      const total = sh.entries.length ? sh.entries[sh.entries.length - 1].cumWeight : 0n;
      let yours = 0n;
      let prev = 0n;
      for (const e of sh.entries) {
        if (e.participant === YOU) yours += e.cumWeight - prev;
        prev = e.cumWeight;
      }
      const sealedByTime = sh.status === 'Open' && now >= sh.cutoff;
      run.shoe = { shoeId: sh.shoeId, objectIndex: sh.objectIndex, status: sealedByTime ? 'Sealed' : sh.status, amount: sh.amount, cutoff: sh.cutoff, totalWeight: total, entries: sh.entries.length, yourWeight: yours, winner: sh.winner, winnerLabel: sh.winner ? this.nameOf(sh.winner) : null, paid: sh.paid };
    }
    const open = this.windowNet.get(w);
    const ps = this.phaseStage();
    const rec = this.run ? this.runs.get(this.run.id) ?? null : null;
    const roadState = this.sim && rec?.open ? this.sim.road : rec?.road ?? null;
    const budget = rec?.budget ?? this.road.runBudget;
    const escrowRec = rec ? { ...rec, road: roadState ?? rec.road } : null;
    const live = this.sim?.road ?? null;
    const biasActive = !!live && live.activeModifierId !== 0n && !!rec?.open;
    const e = this.currentEpoch();
    const accruedNow = this.accrual.get(e) ?? 0n;
    const est = waterfallSplit(accruedNow, this.policy);
    const pol = this.policy;
    const cohortEpoch = rec?.cohortEpoch ?? null;
    const sealedC = cohortEpoch !== null ? this.sealed.get(cohortEpoch) : undefined;
    const credited = cohortEpoch !== null ? this.waterfallEpochs.find((x) => x.epoch === cohortEpoch) : undefined;
    return {
      mode: 'demo',
      modeLabel: 'DEMO — simulated trades, simulated pool, demo randomness. No real funds.',
      networkLabel: 'Demo (in-memory)',
      healthy: true,
      healthNote: this.paused ? 'Demo paused' : 'Simulation running',
      quote: { symbol: 'DEMO-Q', decimals: 18, label: 'Demo quote unit — stands in for USDG; not real money' },
      runner: { symbol: 'RUNNER', decimals: 18, label: 'Demo balances' },
      refSupply: REF_SUPPLY,
      phase: ps.phase,
      stage: ps.stage,
      timing: {
        now,
        currentWindow: w,
        windowSeconds: this.windowSeconds,
        windowEndsAt: (w + 1) * this.windowSeconds,
        nextStepWindow: this.stage === 'active' || this.stage === 'preview' ? (this.run ? this.run.startWindow + (this.sim?.stepIndex ?? 0) : null) : null,
        unresolvedClosed: 0,
        epoch: e,
        epochSeconds: this.epochWindows * this.windowSeconds,
        epochEndsAt: (e + 1) * this.epochWindows * this.windowSeconds,
        // The simulation owns its clock, so a window countdown is honest while steering or previewing.
        countdownTo: this.stage === 'active' ? (w + 1) * this.windowSeconds : this.stage === 'preview' && this.run ? this.run.startWindow * this.windowSeconds : null,
        phaseDetail: ps.detail,
        abandonableAt: null,
      },
      run,
      steps: this.steps,
      openInput: open ? { window: w, ...open } : { window: w, net: 0n, volume: 0n, swaps: 0, yours: 0n },
      road: {
        runId: rec?.id ?? null,
        escrow: escrowRec ? this.escrowDto(escrowRec) : null,
        params: { runBudget: s(this.road.runBudget), minRunBudget: s(this.road.minRunBudget), opsProvision: s(this.road.opsProvision), waterSliceBps: Number(this.road.waterSliceBps), overheatSliceBps: Number(this.road.overheatSliceBps), overheatBiasSteps: Number(this.road.overheatBiasSteps), overheatCap: s(this.road.overheatCap) },
        waterSlice: s((budget * this.road.waterSliceBps) / 10_000n),
        overheatPerStep: s((budget * this.road.overheatSliceBps) / 10_000n),
        effects: (rec?.effects ?? []).map((x) => ({ step: x.step, effect: x.effect, effectName: (['water', 'overheat', 'exit-holders', 'exit-forge'] as const)[x.effect - 1], to: x.to, requested: s(x.requested), assigned: s(x.assigned), txHash: ZERO_HASH, blockNumber: '0' })),
      },
      overheat: {
        active: biasActive,
        modifierId: biasActive ? Number(live!.activeModifierId) : null,
        stepsLeft: biasActive ? Number(live!.modifierStepsLeft) : 0,
        assigned: s(biasActive ? live!.modifierAssigned : 0n),
        cap: s(this.road.overheatCap),
        sliceBps: Number(this.road.overheatSliceBps),
        perStep: s((budget * this.road.overheatSliceBps) / 10_000n),
        modifierCount: Number(this.modifierCount),
      },
      funding: {
        fundable: this.fundableNow() > 0n,
        exhibition: runReservation(this.roadReserve, this.operations, this.road.runBudget, this.road.minRunBudget, this.road.opsProvision).exhibition,
        reason: this.fundingReason(),
        configVersion: 1,
        nextBudget: s(this.fundableNow()),
        roadReserve: s(this.roadReserve),
        operations: s(this.operations),
        runBudget: s(this.road.runBudget),
        minRunBudget: s(this.road.minRunBudget),
        opsProvision: s(this.road.opsProvision),
        fundingPaused: false,
      },
      baseRewards: {
        epoch: e,
        policy: { policyId: 1, fromEpoch: 0, holdersBps: Number(pol.holdersBps), forgeBps: Number(pol.forgeBps), roadBps: Number(pol.roadBps), prizeBps: Number(pol.prizeBps), opsBps: Number(pol.opsBps), holderDeferredBps: Number(pol.holderDeferredBps) },
        openPeriodAccrued: s(accruedNow),
        openPeriodSplitEstimate: Object.fromEntries(Object.entries(est).map(([k, v]) => [k, s(v)])) as BaseRewardsView['openPeriodSplitEstimate'],
        cohortEpoch,
        cohortHolderCredit: credited ? { direct: credited.holdersDirect ?? '0', deferred: credited.holdersDeferred ?? '0', weight: sealedC ? s(sealedC.global) : null, carried: false } : null,
        note: 'Base holder income is split from each settled epoch under its policy and never depends on the run. The run moves only its own reserved road budget. The open-period split is an estimate of fees accrued so far (demo).',
      },
      cohort:
        cohortEpoch !== null
          ? { epoch: cohortEpoch, sealed: true, globalWeight: s(sealedC?.global ?? DEMO_HOLDERS), pendingRoadValue: s(this.cohortPending.get(cohortEpoch) ?? 0n), openRuns: this.cohortOpenRuns.get(cohortEpoch) ?? 0, runFinalizedCount: this.runFinalizedCount }
          : null,
      replayRunId: this.lastReplayRunId,
      treasury: this.treasuryView(),
      buyback: { authorized: this.authorized, spent: this.spent, purchased: this.purchased, inventory: this.inventory, burned: this.burned, status: this.forgeStage, lastNote: this.forgeNote },
      market: { price: price(this.pool), tick: Math.round(Math.log(price(this.pool)) / Math.log(1.0001)), hookFeeBps: 50, lpFeePips: 3000, runnerSupply: this.runnerSupply, liquidityNote: 'Simulated full-range pool (demo)' },
      user: { connected: true, address: YOU, label: 'Demo wallet (simulated balances)', walletKind: 'demo', walletChainId: null, wrongChain: false, walletError: null, runner: this.user.runner, quote: this.user.quote },
      account: this.accountView(),
      rewardsBreakdown: this.breakdownView(),
      runners: this.runnersView(),
      collection: {
        address: ZERO_ADDR,
        name: 'RUNNER Identity (demo)',
        symbol: 'RUNNERID',
        backing: s(this.backing),
        cap: this.cap,
        minted: this.minted,
        live: [...this.identities.values()].filter((t) => t.status === 'live').length,
        intakeOpen: true,
        intakePaused: false,
        principalLiability: s(BigInt([...this.identities.values()].filter((t) => t.status === 'live').length) * this.backing),
        cosmeticRenderer: null,
        baseRenderer: ZERO_ADDR,
      },
      collectionItems: [...this.identities.values()].sort((a, b) => b.id - a.id).map((t) => this.summary(t)),
      versions: this.versionsView(),
      feed: this.feed,
      journal: this.journal,
      freshness: { feed: 'not-used', api: 'not-used', blockNumber: null, lagBlocks: null, stale: false, catchingUp: false, lastOkAt: null, note: this.paused ? 'Demo simulation paused' : 'Demo simulation running in this tab (no API, no chain)' },
      quotePolicy: { supported: true, verified: false, note: 'DEMO-Q is a simulated quote unit, not USDG.' },
      // No chain: the demo runs the local fixture economics (FIXTURE_V1 = DefaultConfig), LOCAL/TEST only, not approved.
      economicProfile: economicProfileSummary({ chainId: 31337, economicProfile: DEFAULT_ECONOMIC_PROFILE }),
      version: this.version,
    };
  }
}

export function fmt(v: bigint, decimals = 18, dp = 2): string {
  const neg = v < 0n;
  const a = neg ? -v : v;
  const base = 10n ** BigInt(decimals);
  const whole = a / base;
  const frac = ((a % base) * 10n ** BigInt(dp)) / base;
  return `${neg ? '-' : ''}${whole.toLocaleString('en-US')}${dp > 0 ? '.' + frac.toString().padStart(dp, '0') : ''}`;
}

export function fmtR(v: bigint): string {
  return fmt(v, 18, 0);
}
